<template>
	<v-container class="index pa-0" fluid syno-id="index-page">
		<v-row class="ma-0">
			<!-- Left Sidebar Toolbar - Full height -->
			<v-col cols="3" v-if="isLogin" class="sidebar-col pa-0" syno-id="sidebar">
				<div class="sidebar-container">
					<div class="sidebar-header">
						<v-icon icon="mdi-tools" class="me-2" />
						<span class="sidebar-title" syno-id="sidebar-title">Actions</span>
					</div>
					<div class="sidebar-content">
						<v-btn
							block
							color="white"
							variant="text"
							@click="logout"
							syno-id="sidebar-sign-out-btn"
							prepend-icon="mdi-logout"
							class="sidebar-btn mb-3"
						>
							Sign out
						</v-btn>
						<v-btn
							v-if="login_method !== 'saml'"
							block
							color="white"
							variant="text"
							@click="testpost"
							syno-id="sidebar-test-post-btn"
							prepend-icon="mdi-post"
							class="sidebar-btn mb-3"
						>
							Test Post
						</v-btn>
						<v-btn
							v-if="login_method !== 'saml'"
							block
							color="white"
							variant="text"
							@click="testquery"
							syno-id="sidebar-test-query-btn"
							prepend-icon="mdi-database-search"
							class="sidebar-btn mb-3"
						>
							Test Query
						</v-btn>
						<v-btn
							v-if="login_method !== 'saml'"
							block
							color="white"
							variant="text"
							@click="getUserV2"
							syno-id="sidebar-get-user-v2-btn"
							prepend-icon="mdi-account-details"
							class="sidebar-btn"
						>
							Get User V2
						</v-btn>
					</div>
				</div>
			</v-col>

			<!-- Main Content - Responsive width -->
			<v-col :cols="isLogin ? 9 : 12" class="main-content-col">
				<div class="main-content">
					<!-- Header Section -->
					<div class="content-header">
						<h1 class="main-title" syno-id="index-title">Account System Login Demo</h1>
					</div>
					
					<!-- Alerts Section -->
					<div class="content-section">
						<v-alert
							v-if="errMsg"
							syno-id="index-message"
							:type="errType || (isLogin ? 'success' : 'info')"
							class="mb-4"
						>
							{{ errMsg }}
						</v-alert>

						<v-alert
							type=""
							color="blue"
							syno-id="index-zone"
							class="mb-4"
							variant="tonal"
						>
							<div class="d-flex align-center">
								<v-icon icon="mdi-earth" class="me-2" />
								<span class="font-weight-medium">Zone: {{ zone }}</span>
							</div>
						</v-alert>

						<v-alert
							v-if="isLogin"
							type=""
							class="mb-4"
							color="green"
							syno-id="user-info"
							variant="tonal"
						>
							<div class="user-info-row mb-2">
								<v-icon icon="mdi-account" class="me-2" />
								<span class="font-weight-medium" syno-id="user-current">User: {{ getCurrentUser() }}</span>
							</div>
							<div class="user-info-row">
								<v-icon icon="mdi-account-cog" class="me-2" />
								<span class="font-weight-medium" syno-id="user-account-type">Account Type: {{ account_type }}</span>
							</div>
						</v-alert>
					</div>

					<!-- User Information Section -->
					<template v-if="isLogin">
						<div class="content-section">
							<div class="welcome-section">
								<h2 class="welcome-title" syno-id="user-welcome">Welcome, {{ display_name }}!</h2>
								<div v-if="login_method !== 'saml'" class="user-details-grid" syno-id="user-details">
									<div class="detail-item" syno-id="user-detail-user-id">
										<v-icon icon="mdi-account" class="detail-icon" />
										<div class="detail-content">
											<div class="detail-label" syno-id="user-detail-user-id-label">User ID</div>
											<div class="detail-value" syno-id="user-detail-user-id-value">{{ userid }}</div>
										</div>
									</div>
									<div class="detail-item" syno-id="user-detail-username">
										<v-icon icon="mdi-account-details" class="detail-icon" />
										<div class="detail-content">
											<div class="detail-label" syno-id="user-detail-username-label">Username</div>
											<div class="detail-value" syno-id="user-detail-username-value">{{ username }}</div>
										</div>
									</div>
									<div class="detail-item" syno-id="user-detail-main-account-id">
										<v-icon icon="mdi-account-group" class="detail-icon" />
										<div class="detail-content">
											<div class="detail-label" syno-id="user-detail-main-account-id-label">Main Account ID</div>
											<div class="detail-value" syno-id="user-detail-main-account-id-value">{{ main_account_id }}</div>
										</div>
									</div>
									<div class="detail-item" syno-id="user-detail-language">
										<v-icon icon="mdi-translate" class="detail-icon" />
										<div class="detail-content">
											<div class="detail-label" syno-id="user-detail-language-label">Language</div>
											<div class="detail-value" syno-id="user-detail-language-value">{{ language }}</div>
										</div>
									</div>
									<div class="detail-item" syno-id="user-detail-country">
										<v-icon icon="mdi-map-marker" class="detail-icon" />
										<div class="detail-content">
											<div class="detail-label" syno-id="user-detail-country-label">Country</div>
											<div class="detail-value" syno-id="user-detail-country-value">{{ country }}</div>
										</div>
									</div>
									<div class="detail-item" syno-id="user-detail-timezone">
										<v-icon icon="mdi-clock-outline" class="detail-icon" />
										<div class="detail-content">
											<div class="detail-label" syno-id="user-detail-timezone-label">Timezone</div>
											<div class="detail-value" syno-id="user-detail-timezone-value">{{ timezone }}</div>
										</div>
									</div>
									<div class="detail-item" syno-id="user-detail-gdpr">
										<v-icon icon="mdi-shield-check" class="detail-icon" />
										<div class="detail-content">
											<div class="detail-label" syno-id="user-detail-gdpr-label">GDPR</div>
											<div class="detail-value" syno-id="user-detail-gdpr-value">{{ gdpr ? 'Enabled' : 'Disabled' }}</div>
										</div>
									</div>
									<div class="detail-item" syno-id="user-detail-account-type">
										<v-icon icon="mdi-account-cog" class="detail-icon" />
										<div class="detail-content">
											<div class="detail-label" syno-id="user-detail-account-type-label">Account Type</div>
											<div class="detail-value" syno-id="user-detail-account-type-value">{{ account_type }}</div>
										</div>
									</div>
									<div class="detail-item" syno-id="user-detail-tenant">
										<v-icon icon="mdi-domain" class="detail-icon" />
										<div class="detail-content">
											<div class="detail-label" syno-id="user-detail-tenant-label">Tenant</div>
											<div class="detail-value" syno-id="user-detail-tenant-value">{{ tenant }}</div>
										</div>
									</div>
									<div class="detail-item" syno-id="user-detail-issuer-zone">
										<v-icon icon="mdi-earth" class="detail-icon" />
										<div class="detail-content">
											<div class="detail-label" syno-id="user-detail-issuer-zone-label">Issuer Zone</div>
											<div class="detail-value" syno-id="user-detail-issuer-zone-value">{{ issuer_zone }}</div>
										</div>
									</div>
								</div>
								<div v-else class="user-details-grid" syno-id="saml-user-info">
									<div class="detail-item" syno-id="saml-name-id">
										<v-icon icon="mdi-card-account-details" class="detail-icon" />
										<div class="detail-content">
											<div class="detail-label" syno-id="saml-name-id-label">NameID</div>
											<div class="detail-value" syno-id="saml-name-id-value">{{ saml_name_id }}</div>
										</div>
									</div>
									<div class="detail-item" syno-id="saml-idp">
										<v-icon icon="mdi-shield-account" class="detail-icon" />
										<div class="detail-content">
											<div class="detail-label" syno-id="saml-idp-label">IdP</div>
											<div class="detail-value" syno-id="saml-idp-value">{{ saml_idp }}</div>
										</div>
									</div>
									<div
										v-for="attr in samlAttributes"
										:key="attr.name"
										class="detail-item"
										:syno-id="`saml-attribute-${attr.id}`"
									>
										<v-icon icon="mdi-tag-text" class="detail-icon" />
										<div class="detail-content">
											<div class="detail-label" :syno-id="`saml-attribute-${attr.id}-label`">{{ attr.name }}</div>
											<div class="detail-value" :syno-id="`saml-attribute-${attr.id}-value`">{{ attr.value }}</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</template>

					<!-- Login Form Section -->
					<template v-else>
						<div class="content-section">
							<div class="login-section">
								<h2 class="section-title" syno-id="login-title">Login</h2>
								<v-form @submit.prevent="login" class="login-form" syno-id="login-form">
									<v-btn
										block
										color="primary"
										size="large"
										type="submit"
										syno-id="login-sign-in-btn"
										prepend-icon="mdi-login"
										class="login-btn"
									>
										Sign in
									</v-btn>
									<v-btn
										v-for="idp in saml_idps"
										:key="idp.id"
										block
										color="secondary"
										size="large"
										variant="outlined"
										prepend-icon="mdi-shield-account"
										class="login-btn"
										:syno-id="`login-saml-${idp.id}-btn`"
										:disabled="idp.enabled === false"
										@click="samlLogin(idp.id)"
									>
										{{ idp.label }}
									</v-btn>
									<v-expansion-panels class="advanced-panels" syno-id="login-advanced">
										<v-expansion-panel elevation="0">
											<v-expansion-panel-title syno-id="login-advanced-toggle">Advanced</v-expansion-panel-title>
											<v-expansion-panel-text>
												<v-text-field
													v-model="state"
													syno-id="login-state-input"
													label="State"
													placeholder="anything..."
													variant="outlined"
													class="mb-3"
												/>
												<v-text-field
													v-model="prompt"
													syno-id="login-prompt-input"
													label="Prompt"
													placeholder="login"
													variant="outlined"
													class="mb-3"
												/>
												<v-text-field
													v-model="login_hint"
													syno-id="login-hint-input"
													label="Login Hint"
													placeholder="user's account"
													variant="outlined"
													class="mb-3"
												/>
												<v-text-field
													v-model="login_hint_editable"
													syno-id="login-hint-editable-input"
													label="Login Hint Editable"
													placeholder="set to false for force login as login_hint"
													variant="outlined"
													class="mb-3"
												/>
												<v-text-field
													v-model="region"
													syno-id="login-region-input"
													label="Region"
													placeholder="eu/us/tw"
													variant="outlined"
													class="mb-3"
												/>
												<v-text-field
													v-model="redirect_url"
													syno-id="login-redirect-url-input"
													label="Redirect URL"
													placeholder="window.location.origin"
													variant="outlined"
													class="mb-3"
												/>
											</v-expansion-panel-text>
										</v-expansion-panel>
									</v-expansion-panels>
								</v-form>
							</div>
						</div>
					</template>
				</div>
			</v-col>
		</v-row>
	</v-container>
</template>

<style scoped>
.index {
	max-width: 1200px;
	margin: 0 auto;
	padding: 24px;
}

.v-card {
	transition: transform 0.2s;
}

.v-card:hover {
	transform: translateY(-2px);
}

.v-btn {
	margin-bottom: 8px;
}

.user-info-item {
	display: flex;
	align-items: center;
	position: relative;
}

.user-info-content {
	margin-left: 16px;
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: 8px;
}

.user-info-title {
	font-weight: bold;
	font-size: 0.875rem;
	color: rgba(var(--v-theme-on-surface), 0.6);
	display: flex;
	align-items: center;
	min-width: 120px;
}

.user-info-item .v-list-item-subtitle {
	font-size: 1rem;
	color: rgba(var(--v-theme-on-surface), 0.87);
	display: flex;
	align-items: center;
	text-align: left;
}

/* Ensure icons are vertically centered with text */
.v-alert .d-flex.align-center {
	align-items: center !important;
	line-height: 1.5;
}

.v-alert .d-flex.align-center .v-icon {
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
}

.v-alert .d-flex.align-center span {
	display: flex;
	align-items: center;
	line-height: 1.5;
}

.user-info-row {
	display: flex;
	align-items: center;
	min-height: 24px;
}

.user-info-row .v-icon {
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
}

.user-info-row span {
	display: flex;
	align-items: center;
}

/* Sidebar styling */
.sidebar-col {
	flex: 0 0 25% !important;
	max-width: 300px !important;
	min-width: 250px !important;
	padding: 0 !important;
}

.sidebar-container {
	position: fixed;
	top: 0;
	left: 0;
	width: 25%;
	max-width: 300px;
	height: 100vh;
	background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
	display: flex;
	flex-direction: column;
	z-index: 100;
}

.sidebar-header {
	padding: 24px 20px 16px 20px;
	border-bottom: 1px solid rgba(255, 255, 255, 0.1);
	display: flex;
	align-items: center;
}

.sidebar-title {
	color: white;
	font-size: 1.25rem;
	font-weight: 600;
}

.sidebar-content {
	padding: 20px;
	flex: 1;
}

.sidebar-btn {
	justify-content: flex-start !important;
	text-align: left !important;
	padding: 12px 16px !important;
	border-radius: 8px !important;
	transition: all 0.3s ease !important;
	text-transform: none !important;
	font-weight: 500 !important;
}

.sidebar-btn:hover {
	background-color: rgba(255, 255, 255, 0.1) !important;
	transform: translateX(4px);
}

.sidebar-btn .v-icon {
	margin-right: 12px !important;
}

/* Main content styling */
.main-content-col {
	flex: 1;
	min-width: 0;
}

.main-content {
	padding: 24px;
	display: flex;
	flex-direction: column;
	align-items: center;
	width: 100%;
	height: 100vh;
	overflow-y: auto;
}

.content-header {
	text-align: center;
	margin-bottom: 24px;
	width: 100%;
}

.main-title {
	font-size: 2.5rem;
	font-weight: 700;
	color: #2c3e50;
	margin: 0;
}

.content-section {
	margin-bottom: 24px;
	width: 100%;
	max-width: 1200px;
}

.welcome-section {
	background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
	border-radius: 16px;
	padding: 24px;
	border: 1px solid #dee2e6;
	width: 100%;
}

.welcome-title {
	font-size: 1.75rem;
	font-weight: 600;
	color: #495057;
	margin-bottom: 20px;
	text-align: center;
}

.user-details-grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
	gap: 16px;
	width: 100%;
}

.detail-item {
	display: flex;
	align-items: center;
	padding: 16px;
	background: white;
	border-radius: 12px;
	border: 1px solid #e9ecef;
	transition: all 0.3s ease;
}

.detail-item:hover {
	transform: translateY(-2px);
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.detail-icon {
	color: #667eea;
	margin-right: 16px;
	font-size: 1.5rem;
}

.detail-content {
	flex: 1;
}

.detail-label {
	font-size: 0.875rem;
	font-weight: 600;
	color: #6c757d;
	margin-bottom: 4px;
	text-transform: uppercase;
	letter-spacing: 0.5px;
}

.detail-value {
	font-size: 1rem;
	font-weight: 500;
	color: #495057;
}

.login-section {
	background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
	border-radius: 16px;
	padding: 32px;
	border: 1px solid #dee2e6;
	width: 100%;
	max-width: 600px;
	margin: 0 auto;
}

.section-title {
	font-size: 1.75rem;
	font-weight: 600;
	color: #495057;
	margin-bottom: 24px;
	text-align: center;
}

.login-form {
	width: 100%;
	max-width: 400px;
	margin: 0 auto;
}

.login-btn {
	margin-top: 20px;
	height: 48px;
	font-size: 1.1rem;
	font-weight: 600;
}

.advanced-panels {
	margin-top: 20px;
}

@media (max-width: 959px) {
	.sidebar-container {
		width: 200px;
		max-width: 200px;
	}
	.sidebar-col {
		flex: 0 0 200px !important;
		max-width: 200px !important;
		min-width: 180px !important;
	}
}

@media (max-width: 599px) {
	.sidebar-container {
		width: 180px;
		max-width: 180px;
	}
	.sidebar-col {
		flex: 0 0 180px !important;
		max-width: 180px !important;
		min-width: 160px !important;
	}
}
</style>

<script>
import LoginManager from '@synology-hct/syno-c2-account-js-sdk'
import { env } from '../utils/env.js'

// Covers a healthy configure + /v1/user (well under 1 s); past this the SDK is treated as stuck
const OIDC_SETTLE_TIMEOUT_MS = 3000

function waitAtMost(promise, ms) {
	let timer
	return Promise.race([
		promise,
		new Promise((resolve) => { timer = setTimeout(resolve, ms) }),
	]).finally(() => clearTimeout(timer))
}

// Attribute names may be URIs (e.g. http://schemas.xmlsoap.org/...), so reduce them to a selector-safe syno-id suffix
function toKebabCase(name) {
	return name
		.replace(/([a-z0-9])([A-Z])/g, '$1-$2')
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '')
}

export default {
	name: 'Index',
	data() {
		return {
			state: '',
			prompt: '',
			region: '',
			login_hint: '',
			login_hint_editable: '',
			redirect_url: '',
			errMsg: "",
			errType: "",
			isLogin: false,
			username: "",
			display_name: null,
			tenant: null,
			issuer_zone: null,
			userid: null,
			main_account_id: null,
			language: "",
			timezone: "",
			country: "",
			gdpr: false,
			account_type: null,
			login_method: null,
			saml_idps: [],
			saml_idp: null,
			saml_name_id: null,
			saml_attributes: {},
			zone: env.ZONE,
			loginManagerHost: env.LOGIN_MANAGER_HOST,
			apiBasePath: env.API_BASE_PATH,
			globalSSOHost: env.GLOBAL_SSO_HOST,
			logLevel: env.DEBUG ? 'debug' : 'info'
		}
	},
	async mounted() {
		if (env.DEBUG) {
			// print runtime variables
			for (const key in env) { 
				console.log(key+":", env[key])
			}
		}
		// overwrite url for getting query params since vue-router hash mode treat redirect_URL with query params as search
		// reference: https://github.com/vuejs/vue-router/issues/2125#issuecomment-519521424
		/*if (location.search) {
			location.replace(location.pathname + location.hash + location.search)
		}
		if (this.$route.query.error) {
			this.errMsg = this.$route.query.error
			if (this.$route.query.error_description) {
				this.errMsg += ": " + this.$route.query.error_description
			}
			if (this.$route.query.error_uri) {
				this.errMsg += ": " + this.$route.query.error_uri
			}
		}*/

		let settleOidc
		const oidcSettled = new Promise((resolve) => { settleOidc = resolve })
		// The hint cookie means an OIDC session may win; hold the SAML view so it does not flash first.
		// hasWebAccessCookie() is a plain cookie read and needs no configure().
		const samlGate = LoginManager.hasWebAccessCookie() ? oidcSettled : null

		// configure() can hang on a websyncer redirect or reject, so SAML must not wait for it
		this.samlInit(samlGate).catch((err) => console.error("SAML init failed:", err))

		try {
			await this.oidcInit()
		} finally {
			settleOidc()
		}
	},
	computed: {
		samlAttributes() {
			// Reserve -label/-value too: attribute "x-label" would otherwise reuse the label id of "x"
			const used = new Set()
			const free = (id) => !used.has(id) && !used.has(`${id}-label`) && !used.has(`${id}-value`)
			return Object.keys(this.saml_attributes).map((name, index) => {
				const base = toKebabCase(name) || String(index)
				let id = base
				for (let n = 2; !free(id); n++) {
					id = `${base}-${n}`
				}
				used.add(id).add(`${id}-label`).add(`${id}-value`)
				return { name, id, value: [].concat(this.saml_attributes[name]).join(', ') }
			})
		},
	},
	methods: {
		oidcInit: async function() {
			try {
				await LoginManager.configure({
					zone: this.zone,
					host: this.loginManagerHost,
					basePath: this.apiBasePath,
					storage: window.localStorage,
					globalSSOHost: this.globalSSOHost,
					logLevel: this.logLevel,
				})
			} catch (err) {
				console.error("LoginManager configure failed:", err)
				return
			}
			if (env.DEBUG) {
				console.log("LoginManager configure completed")
			}

			/* eslint-disable */
			let uri = window.location.search.substring(1)
			let params = new URLSearchParams(uri)
			if (window.location.pathname === "/login") {
				this.$router.push({
					name: "Callback"
				})
			} else if (window.location.pathname === "/loginform") {
				this.$router.push({
					name: "Loginform"
				})
			} else if (LoginManager.hasWebAccessCookie()) {
				try {
					await this.userApi()
				} catch (err) {
					console.error("OIDC user fetch failed:", err)
				}
			}
		},
		userApi: async function() {
			const user = await this.getUser()

			if (user) {
				this.isLogin = true;
				this.login_method = 'oidc'
				this.display_name = user.name
				this.username = user.username
				this.userid = user.id
				this.email = user.email
				this.main_account_id = user.main_account_id
				this.account_type = user.account_type
				this.tenant = user.tenant
				this.issuer_zone = user.issuer_zone
				this.language = user.language
				this.country = user.country
				this.timezone= user.timezone
				this.gdpr = user.gdpr
			}
		},
		samlInit: async function(gate) {
			// A failed status call still tries /user; only a known-empty IdP list skips it
			const statusOk = await this.samlStatusApi()
			if (!statusOk || this.saml_idps.length > 0) {
				await this.samlUserApi(gate)
			}
		},
		samlStatusApi: async function() {
			const resp = await fetch('/noauth/saml/status', { credentials: 'include' }).catch(() => null)

			if (!resp || 200 !== resp.status) {
				return false
			}
			const body = await resp.json().catch(() => null)
			if (!body || !Array.isArray(body.idps)) {
				return false
			}
			this.saml_idps = body.idps
				.filter((idp) => idp && typeof idp.id === 'string' && idp.id)
				.map((idp) => ({
					id: idp.id,
					label: typeof idp.label === 'string' && idp.label ? idp.label : `SAML Login(${idp.id})`,
					enabled: idp.enabled,
				}))
			return true
		},
		samlUserApi: async function(gate) {
			const resp = await fetch('/noauth/saml/user', { credentials: 'include' }).catch(() => null)

			if (!resp || 200 !== resp.status) {
				return
			}
			const user = await resp.json().catch(() => null)
			if (!user || typeof user !== 'object') {
				return
			}
			if (gate) {
				await waitAtMost(gate, OIDC_SETTLE_TIMEOUT_MS)
			}
			// The OIDC session takes precedence when both exist
			if ('oidc' === this.login_method) {
				return
			}
			this.isLogin = true
			this.login_method = 'saml'
			this.account_type = 'saml'
			this.saml_name_id = user.name_id
			this.saml_idp = user.idp
			this.display_name = user.name_id
			this.saml_attributes = user.attributes || {}
		},
		getCurrentUser() {
			if (!this.isLogin) {
				return ''
			}
			switch(this.account_type) {
				case 'c2':
					return `${this.username}@${this.tenant}.c2.${this.issuer_zone}`
				case 'saml':
					return this.saml_name_id
				default:
					return this.email
			}
		},
		getUser: async function() {
			const resp = await LoginManager.sendRequest('/v1/user')

			if (200 === resp.status) {
				return await resp.json()
			}

			return null
		},
		login: async function() {
			const redirect_to = await LoginManager.login({
					doRedirect:false,
					queries: {
						service_key: "app1",
					...({ redirect_url: this.redirect_url || window.location.origin }),
					...(this.state ? { state: this.state } : { state: 'teststate123' }),
					...(this.prompt ? { prompt: this.prompt } : {}),
					...(this.region ? { region: this.region } : {}),
					...(this.login_hint ? { login_hint: this.login_hint } : {}),
					...(this.login_hint_editable ? { login_hint_editable: this.login_hint_editable }: {}),
				}
			})
			window.location = redirect_to + "&login_portal=1"
			await new Promise(() => {})
		},
		samlLogin: function(id) {
			window.location.href = `/noauth/saml/${encodeURIComponent(id)}/login`
		},
		logout: async function() {
			if ('saml' === this.login_method) {
				let data
				try {
					// The backend rejects logout without this header (CSRF guard)
					const resp = await fetch('/noauth/saml/logout', {
						method: 'POST',
						credentials: 'include',
						headers: { 'X-Requested-With': 'fetch' },
					})
					if (!resp.ok) {
						throw new Error(`HTTP ${resp.status}`)
					}
					// The local session is already gone; an unreadable body still ends at '/'
					data = await resp.json().catch(() => ({}))
				} catch (err) {
					this.errType = 'error'
					this.errMsg = `SAML logout failed: ${err.message}`
					return
				}
				const target = data && typeof data.redirect_url === 'string' ? data.redirect_url : ''
				window.location.href = target || '/'
				return
			}
			await LoginManager.logout({queries: {redirect_url: window.location.origin, state: "test_logout_state"}})
		},
		testpost: async function() {
			const resp = await LoginManager.sendRequest('/v1/testpost', {
				method: 'POST',
				body: JSON.stringify({
					b1: 123,
					b2: false,
					b3: {
						b4: 'fuck',
					},
				}),
			})

			if (200 === resp.status) {
				console.log(await resp.json())
			}
		},
		testquery: async function() {
			const resp = await LoginManager.sendRequest('/v1/user?q=123')

			if (200 === resp.status) {
				console.log(await resp.json())
			}
		},
		getUserV2: async function() {
			const resp = await LoginManager.sendRequest('/backend/auth/api/v2/user')
			if (200 === resp.status) {
				console.log(await resp.json())
			}
		}
	}
}
</script>
